import { CLOCK } from "@eldrun/content";

/** O que o cliente lê do schema `clock`. */
export interface ClockLike {
  day: number;
  night: boolean;
  elapsed: number;
  duration: number;
}

export interface ClockReading {
  day: number;
  night: boolean;
  /** Segundos desde o início da fase (extrapolado) e que faltam para ela acabar. */
  elapsed: number;
  remaining: number;
  /** Progresso da fase, de 0 a 1. */
  progress: number;
  /** Fase da atmosfera (0 nascer, 0,25 meio-dia, 0,5 pôr do sol, 0,75 meia-noite). */
  phase: number;
  /** Boca da Noite: últimos 30 s do dia. */
  dusk: boolean;
}

/** Fase da atmosfera antes de a partida ter relógio (meio da manhã). */
export const DEFAULT_PHASE = 0.15;

/** O dia ocupa a metade clara do ciclo da atmosfera e a noite, a escura. */
export function atmospherePhase(night: boolean, progress: number): number {
  const k = Math.min(1, Math.max(0, progress));
  return night ? 0.5 + 0.5 * k : 0.5 * k;
}

/**
 * Relógio do servidor no cliente (doc 06, Relógio do dia): o schema traz o tempo da fase uma vez
 * por segundo, e entre os patches o cliente extrapola com o próprio relógio. Nenhuma regra depende
 * disto; só a atmosfera e o HUD.
 */
export class ClockTracker {
  /** Âncora da extrapolação e o último valor que veio do servidor. */
  private anchor = { day: -1, night: false, elapsed: 0, duration: 0, at: 0, server: 0 };

  read(clock: ClockLike, nowMs: number): ClockReading {
    const a = this.anchor;
    const samePhase = clock.day === a.day && clock.night === a.night && clock.duration === a.duration;
    if (!samePhase) {
      this.anchor = { day: clock.day, night: clock.night, elapsed: clock.elapsed, duration: clock.duration, at: nowMs, server: clock.elapsed };
    } else if (clock.elapsed !== a.server) {
      // Patch novo na mesma fase: nunca volta no tempo (um patch atrasado chega com um valor menor
      // que o extrapolado); só salta para a frente quando o servidor está adiantado.
      const current = a.elapsed + (nowMs - a.at) / 1000;
      this.anchor = { ...a, elapsed: Math.min(clock.duration, Math.max(current, clock.elapsed)), at: nowMs, server: clock.elapsed };
    }
    const b = this.anchor;
    if (b.duration <= 0) return { day: b.day, night: false, elapsed: 0, remaining: 0, progress: 0, phase: DEFAULT_PHASE, dusk: false };
    const elapsed = Math.min(b.duration, b.elapsed + (nowMs - b.at) / 1000);
    const remaining = b.duration - elapsed;
    const progress = elapsed / b.duration;
    return { day: b.day, night: b.night, elapsed, remaining, progress, phase: atmospherePhase(b.night, progress), dusk: !b.night && remaining <= CLOCK.dusk };
  }
}
